// HSSNA HTTP 错误类型 — 统一错误对象 (extends Error)
// 规格: ADR-0010 TanStack Query / SPEC §4.4 RFC 7807 Problem Details
// 关联: P1-6 (本文件) / P1-4 (query-client.ts) / P1-5 (query-keys.ts)
//
// 设计:
//   - 作为 TanStack Query 的 TError 类型,在 retry / onError / 全局订阅中统一识别
//   - 携带 HTTP 状态码 + HSSNA 错误码 (HSSNA-XXX-XXX) + RFC 7807 detail
//   - retryable getter: 401/403/404 等 4xx 不重试,仅 5xx / 408 / 429 重试
//   - 兼容现有 HssnaError interface (errors/types.ts),二者并存:
//     * HssnaError: RFC 7807 解析后的平面对象 (用于 handleError 动作路由)
//     * HttpError:   抛出的 Error 子类 (用于 TanStack Query retry/onError 链路)

/**
 * HSSNA 统一 HTTP 错误
 *
 * @example
 * ```ts
 * try {
 *   const res = await fetch('/v1/alerts');
 *   if (!res.ok) {
 *     throw new HttpError({ status: res.status, message: '请求失败' });
 *   }
 * } catch (e) {
 *   if (isHttpError(e) && !e.retryable) {
 *     // 4xx 不重试
 *   }
 * }
 * ```
 */
export class HttpError extends Error {
    /** HTTP 状态码 */
    readonly status: number;
    /** HSSNA 错误码 (HSSNA-<状态码>-<序号>) */
    readonly code?: string;
    /** 链路追踪 ID (W3C Trace Context, 对应 X-Trace-Id) */
    readonly traceId?: string;
    /** 请求 ID (对应 X-Request-Id) */
    readonly requestId?: string;
    /** RFC 7807 Problem Details detail (人类可读) */
    readonly detail?: string;
    /** 字段级校验错误 (422 等) */
    readonly fieldErrors?: Array<{ field: string; message: string }>;

    constructor(opts: {
        status: number;
        message: string;
        code?: string;
        traceId?: string;
        requestId?: string;
        detail?: string;
        fieldErrors?: Array<{ field: string; message: string }>;
    }) {
        super(opts.message);
        this.name = 'HttpError';
        this.status = opts.status;
        this.code = opts.code;
        this.traceId = opts.traceId;
        this.requestId = opts.requestId;
        this.detail = opts.detail;
        this.fieldErrors = opts.fieldErrors;
        // 恢复原型链 (extends Error 在编译后可能丢失 instanceof;统一调用以保持一致)
        Object.setPrototypeOf(this, HttpError.prototype);
    }

    /**
     * 是否可重试
     * - 5xx 服务端错误: 重试
     * - 408 Request Timeout: 重试
     * - 429 Too Many Requests: 重试 (配合退避策略)
     * - 401/403/404 等其他 4xx: 不重试
     *   (ADR-0007: 401/403 触发 token 刷新或登出;404 资源不存在重试无意义)
     */
    get retryable(): boolean {
        return this.status >= 500 || this.status === 408 || this.status === 429;
    }
}

/**
 * 类型守卫:判断 unknown 是否为 HttpError
 *
 * @example
 * ```ts
 * queryClient.getQueryCache().subscribe((event) => {
 *   if (event.type === 'updated' && event.action.type === 'error') {
 *     const error = event.action.error;
 *     if (isHttpError(error)) {
 *       Sentry.captureException(error, { tags: { code: error.code, status: error.status } });
 *     }
 *   }
 * });
 * ```
 */
export function isHttpError(e: unknown): e is HttpError {
    return e instanceof HttpError;
}
